/*
 OK FBI — Public primitive block renderers (Visual Editor · Slice V0).

 Renders any primitive block that appears in `page_content` for a public
 page. Nine primitive `type` values are supported:
   banner · image · video · text · heading · button · spacer · gallery · divider

 Design tokens map to Tailwind so mobile responsiveness stays intact:
   width_desktop / width_mobile → max-width classes
   padding_y                    → py-N classes
   corner                       → rounded-* classes
   aspect                       → aspect-ratio classes
   align                        → mx-auto / mr-auto / ml-auto (RTL-safe via items-alignment)

 Unknown primitive types fail SAFE — component returns null so the public
 page never crashes on a block the frontend doesn't know how to render.
*/
import React from "react";
import { Link } from "react-router-dom";

const PRIMITIVE_TYPES = new Set([
  "banner", "image", "video", "text", "heading",
  "button", "spacer", "gallery", "divider",
]);

export const isPrimitiveBlock = (t) => PRIMITIVE_TYPES.has(String(t || "").toLowerCase());

// ── Token → Tailwind maps ─────────────────────────────────────────────
const WIDTH_D = {
  full: "max-w-none",  xl: "max-w-6xl", lg: "max-w-4xl",
  md:   "max-w-2xl",   sm: "max-w-md",
};
const WIDTH_M = {
  full: "sm:max-w-none",  xl: "sm:max-w-6xl", lg: "sm:max-w-4xl",
  md:   "sm:max-w-2xl",   sm: "sm:max-w-md",
};
const PAD_Y   = { sm: "py-4", md: "py-8", lg: "py-16", xl: "py-24" };
const CORNER  = { sharp: "rounded-none", soft: "rounded-2xl", pill: "rounded-full" };
const ASPECT  = {
  auto: "", "16:9": "aspect-[16/9]", "4:3": "aspect-[4/3]", "3:2": "aspect-[3/2]",
  "1:1": "aspect-square", "21:9": "aspect-[21/9]",
};
const ALIGN_ROW  = { start: "justify-start", center: "justify-center", end: "justify-end" };
const ALIGN_TEXT = { start: "text-start",    center: "text-center",    end: "text-end" };
const SPACER_H   = { sm: "h-4", md: "h-10", lg: "h-20", xl: "h-32" };
const TEXT_TONE  = {
  body:   "text-white",
  muted:  "text-zinc-400",
  accent: "text-[#C5A059]",
};
const TEXT_SIZE  = { sm: "text-sm", md: "text-base", lg: "text-lg" };
const HEADING_LVL = {
  h1: { tag: "h1", cls: "text-4xl sm:text-5xl lg:text-6xl font-cairo font-bold" },
  h2: { tag: "h2", cls: "text-3xl sm:text-4xl font-cairo font-bold" },
  h3: { tag: "h3", cls: "text-2xl sm:text-3xl font-cairo font-semibold" },
  h4: { tag: "h4", cls: "text-xl  sm:text-2xl font-cairo font-semibold" },
};
const BTN_VARIANT = {
  primary: "bg-[#C5A059] text-black hover:bg-[#d9b774]",
  ghost:   "bg-transparent text-white hover:bg-white/10",
  outline: "border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10",
};
const BTN_SIZE = { sm: "px-3 py-1 text-xs", md: "px-5 py-2 text-sm", lg: "px-8 py-3 text-base" };
const GAL_COLS_D = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };
const GAL_COLS_M = { 1: "grid-cols-1", 2: "grid-cols-2" };
const DIV_STYLE = { line: "border-t", dashed: "border-t border-dashed", dotted: "border-t border-dotted" };
const DIV_TONE  = { body: "border-white/50", muted: "border-white/15", accent: "border-[#C5A059]/60" };

// ── Helpers ───────────────────────────────────────────────────────────
const isInternalLink = (href) =>
  typeof href === "string" && href.length > 0 && href.startsWith("/") && !href.startsWith("//");

/** Wraps children in an <a>/<Link> when a link URL is present, otherwise
 *  returns children as-is. Never crashes on empty/undefined links. */
function LinkWrap({ href, target = "_self", className, children }) {
  if (!href) return <>{children}</>;
  if (isInternalLink(href)) {
    return <Link to={href} className={className}>{children}</Link>;
  }
  return (
    <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined}
       className={className}>
      {children}
    </a>
  );
}

const containerCls = (f, extra = "") => {
  const wD = WIDTH_D[f.width_desktop] || WIDTH_D.full;
  const wM = WIDTH_M[f.width_mobile]  || WIDTH_M.full;
  const py = PAD_Y[f.padding_y] || PAD_Y.md;
  return `w-full ${py} px-4 ${extra}`.trim();
};

const innerCls = (f) => {
  const wD = WIDTH_D[f.width_desktop] || WIDTH_D.full;
  const align = f.align === "start" ? "mr-auto" : f.align === "end" ? "ml-auto" : "mx-auto";
  // Mobile first: default to full width, expand on ≥sm via WIDTH_M
  const wM = (f.width_mobile && f.width_mobile !== "full") ? WIDTH_D[f.width_mobile] || "" : "max-w-none";
  return `${wM} sm:${wD} ${align}`.trim();
};

// ── Individual primitive renderers ────────────────────────────────────
function BannerBlock({ f, id }) {
  const src = f.media_url || "";
  const isVideo = String(f.media_kind || "image").toLowerCase() === "video";
  const aspectCls = ASPECT[f.aspect] || ASPECT["21:9"];
  const corner = CORNER[f.corner] || CORNER.sharp;
  const media = !src ? (
    <div className={`w-full ${aspectCls} bg-black/40 border border-white/10 flex items-center justify-center text-zinc-500 text-xs font-cairo`}>
      بنر بدون وسائط
    </div>
  ) : isVideo ? (
    <video src={src} className={`w-full h-full object-cover ${corner}`}
           autoPlay={!!f.autoplay} muted={f.muted !== false}
           loop={!!f.loop} controls={!!f.controls}
           poster={f.poster_url || undefined} playsInline />
  ) : (
    <img src={src} alt={f.alt || ""} loading="lazy"
         className={`w-full h-full object-cover ${corner}`} />
  );
  return (
    <section data-block-id={id} data-block-type="banner" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <LinkWrap href={f.link} target={f.link_target || "_self"}
                  className={`block ${aspectCls} overflow-hidden ${corner}`}>
          {media}
        </LinkWrap>
      </div>
    </section>
  );
}

function ImageBlock({ f, id }) {
  const src = f.media_url || "";
  const corner = CORNER[f.corner] || CORNER.sharp;
  const aspectCls = ASPECT[f.aspect] || "";
  if (!src) return null;
  const img = (
    <img src={src} alt={f.alt || ""} loading="lazy"
         className={`w-full h-auto ${aspectCls} object-cover ${corner}`} />
  );
  return (
    <section data-block-id={id} data-block-type="image" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <LinkWrap href={f.link} target={f.link_target || "_self"}
                  className="block w-full">
          {img}
        </LinkWrap>
      </div>
    </section>
  );
}

function VideoBlock({ f, id }) {
  const src = f.media_url || "";
  const corner = CORNER[f.corner] || CORNER.sharp;
  const aspectCls = ASPECT[f.aspect] || ASPECT["16:9"];
  if (!src) return null;
  return (
    <section data-block-id={id} data-block-type="video" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <div className={`w-full ${aspectCls} overflow-hidden ${corner}`}>
          <video src={src} className="w-full h-full object-cover"
                 autoPlay={!!f.autoplay} muted={f.muted !== false}
                 loop={!!f.loop} controls={f.controls !== false}
                 poster={f.poster_url || undefined} playsInline />
        </div>
      </div>
    </section>
  );
}

function TextBlock({ f, id }) {
  const alignCls = ALIGN_TEXT[f.align] || ALIGN_TEXT.start;
  const tone = TEXT_TONE[f.tone] || TEXT_TONE.body;
  const size = TEXT_SIZE[f.size] || TEXT_SIZE.md;
  const text = String(f.text || "");
  if (!text.trim()) return null;
  return (
    <section data-block-id={id} data-block-type="text" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <p className={`${alignCls} ${tone} ${size} font-cairo leading-relaxed whitespace-pre-line`}>
          {text}
        </p>
      </div>
    </section>
  );
}

function HeadingBlock({ f, id }) {
  const alignCls = ALIGN_TEXT[f.align] || ALIGN_TEXT.start;
  const tone = TEXT_TONE[f.tone] || TEXT_TONE.accent;
  const cfg = HEADING_LVL[String(f.level || "h2").toLowerCase()] || HEADING_LVL.h2;
  const text = String(f.text || "");
  if (!text.trim()) return null;
  const Tag = cfg.tag;
  return (
    <section data-block-id={id} data-block-type="heading" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <Tag className={`${cfg.cls} ${alignCls} ${tone}`}>{text}</Tag>
      </div>
    </section>
  );
}

function ButtonBlock({ f, id }) {
  const text = String(f.text || "").trim();
  if (!text) return null;
  const variant = BTN_VARIANT[f.variant] || BTN_VARIANT.primary;
  const size = BTN_SIZE[f.size] || BTN_SIZE.md;
  const alignRow = ALIGN_ROW[f.align] || ALIGN_ROW.start;
  const btn = (
    <span className={`inline-flex items-center gap-2 font-cairo transition ${variant} ${size} rounded-sm`}>
      {text}
    </span>
  );
  return (
    <section data-block-id={id} data-block-type="button" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <div className={`flex ${alignRow}`}>
          <LinkWrap href={f.link} target={f.link_target || "_self"} className="">{btn}</LinkWrap>
        </div>
      </div>
    </section>
  );
}

function SpacerBlock({ f, id }) {
  const h = SPACER_H[f.size] || SPACER_H.md;
  return <div data-block-id={id} data-block-type="spacer" className={`w-full ${h}`} aria-hidden />;
}

function GalleryBlock({ f, items = [], id }) {
  const list = items.filter((i) => i && i.visible !== false && i.media_url);
  if (list.length === 0) return null;
  const cM = GAL_COLS_M[Number(f.columns_mobile) || 1] || GAL_COLS_M[1];
  const cD = GAL_COLS_D[Number(f.columns_desktop) || 3] || GAL_COLS_D[3];
  const aspectCls = ASPECT[f.aspect] || ASPECT["1:1"];
  return (
    <section data-block-id={id} data-block-type="gallery" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <div className={`grid gap-3 ${cM} ${cD}`}>
          {list.map((it) => (
            <LinkWrap key={it.id} href={it.link} target={it.link_target || "_self"}
                      className={`block overflow-hidden ${aspectCls} bg-black/40`}>
              <img src={it.media_url} alt={it.alt || ""} loading="lazy"
                   className="w-full h-full object-cover" />
            </LinkWrap>
          ))}
        </div>
      </div>
    </section>
  );
}

function DividerBlock({ f, id }) {
  const style = DIV_STYLE[f.style] || DIV_STYLE.line;
  const tone = DIV_TONE[f.tone] || DIV_TONE.muted;
  return (
    <section data-block-id={id} data-block-type="divider" className={containerCls(f)}>
      <div className={innerCls(f)}>
        <hr className={`${style} ${tone}`} />
      </div>
    </section>
  );
}

// ── Dispatcher ────────────────────────────────────────────────────────
/** Render a single primitive block. Returns null for unknown types so the
 *  public page NEVER crashes on a schema it doesn't recognise. */
export function PrimitiveBlock({ section }) {
  if (!section || section.visible === false) return null;
  const t = String(section.type || "").toLowerCase();
  const f = section.fields || {};
  const items = section.items || [];
  const id = section.id;
  switch (t) {
    case "banner":   return <BannerBlock  f={f} id={id} />;
    case "image":    return <ImageBlock   f={f} id={id} />;
    case "video":    return <VideoBlock   f={f} id={id} />;
    case "text":     return <TextBlock    f={f} id={id} />;
    case "heading":  return <HeadingBlock f={f} id={id} />;
    case "button":   return <ButtonBlock  f={f} id={id} />;
    case "spacer":   return <SpacerBlock  f={f} id={id} />;
    case "gallery":  return <GalleryBlock f={f} items={items} id={id} />;
    case "divider":  return <DividerBlock f={f} id={id} />;
    default: return null; // unknown → fail safe
  }
}

/** Renders any primitive blocks that appear on a given page's sections list,
 *  in section-array order, skipping composite/known-composite types silently.
 *  Composite sections (`hero`, `about`, `case_preview`, …) are handled by
 *  the existing bespoke page components — this dispatcher never touches
 *  them. So current public output stays visually identical until an OWNER
 *  inserts a primitive block into `page_content`.
 */
export function PrimitiveBlockList({ sections = [], className = "" }) {
  const items = (sections || []).filter(
    (s) => s && isPrimitiveBlock(s.type) && s.visible !== false
  );
  if (items.length === 0) return null;
  return (
    <div className={className} data-testid="primitive-blocks-layer">
      {items.map((s) => <PrimitiveBlock key={s.id} section={s} />)}
    </div>
  );
}

export default PrimitiveBlockList;
